import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";

import { getSupabase, isSupabaseConfigured } from "./supabase";
import { flushNow, resetTelemetryIdentity, track } from "./telemetry";

/**
 * Auth helpers for MER-115 Phase 5. Wraps Supabase Auth's
 * password sign-in + session subscription so React components
 * can use a single `useAuth` hook.
 *
 * Tenant resolution: a Supabase `tenant_members` row joins
 * `auth.users(id)` to `tenants(id)`. We look up the slug for
 * the currently-authenticated user to gate the per-customer
 * routes (`/dws`, `/bestdisposal`). When MFA is on, Supabase
 * Auth surfaces it via `signInWithPassword` returning an
 * `MFAEnrollFactorResponse` that the Login page handles.
 */

export interface AuthState {
  session: Session | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * MER-186 + MER-193: checks the `is_staff` boolean carried in the
 * authenticated session's JWT `app_metadata` claim. Pure function so
 * StaffGate + any future admin UI can read it synchronously off the
 * session already in React state. The claim is granted via the
 * manual SQL in `dashboard/supabase/STAFF_SETUP.md`. Falls back to
 * false for signed-out sessions and for users without the claim.
 */
export function isStaff(session: Session | null | undefined): boolean {
  if (!session?.user) return false;
  const meta = session.user.app_metadata as { is_staff?: unknown } | undefined;
  return meta?.is_staff === true;
}

export async function getSession(): Promise<Session | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

export async function signInWithEmail(email: string, password: string): Promise<Session> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  if (!data.session) throw new Error("Sign-in returned no session");
  // MER-208: new session, new telemetry identity.
  resetTelemetryIdentity();
  track("login", { method: "password" });
  return data.session;
}

export async function signOut(): Promise<void> {
  // MER-208: record + flush BEFORE revoking the JWT; afterwards the
  // RLS insert would be rejected and the event silently lost. flushNow
  // is bounded (1.5s) and exception-free, so sign-out cannot hang.
  track("sign_out");
  await flushNow();
  const supabase = getSupabase();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  resetTelemetryIdentity();
}

/**
 * MER-193 followup: force-refresh the access token so any custom JWT
 * claims granted after the current session was issued (e.g., a fresh
 * `is_staff` flag landed via `STAFF_SETUP.md`) land in the in-memory
 * Session without forcing the user to sign out + back in. Returns the
 * refreshed Session or null if refresh failed (e.g., revoked refresh
 * token). Callers should treat null as "still not staff" rather than
 * throwing, since a failed refresh is a normal state for a long-idle
 * session and the user can recover via the Sign out button.
 */
export async function refreshSession(): Promise<Session | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase.auth.refreshSession();
  if (error) return null;
  return data.session ?? null;
}

/**
 * Subscribe-and-poll hook for the auth session. Returns the active
 * session plus loading + error state. Re-renders when the user signs
 * in or out anywhere in the app.
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    session: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    // Pre-Phase-6 dev workflow: when Supabase isn't configured, treat
    // the user as signed-out instead of crashing the tree. Login page
    // surfaces the same outcome (a friendly "set env vars" message).
    if (!isSupabaseConfigured()) {
      setState({
        session: null,
        isLoading: false,
        error: new Error("Supabase is not configured for this dashboard build."),
      });
      return () => {
        cancelled = true;
      };
    }

    getSession()
      .then((session) => {
        if (cancelled) return;
        setState({ session, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          session: null,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    let unsub: (() => void) | null = null;
    try {
      const supabase = getSupabase();
      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        if (cancelled) return;
        // MER-208: any session transition (sign-in, expiry, refresh,
        // revocation) invalidates the cached telemetry identity so
        // events are never attributed to a stale user/tenant. The
        // identity re-resolves lazily on the next flush.
        resetTelemetryIdentity();
        setState({ session, isLoading: false, error: null });
      });
      unsub = () => sub.subscription.unsubscribe();
    } catch (err) {
      // Defensive: a malformed env value still throws inside
      // createClient. Surface as state, not as an unmount.
      if (!cancelled) {
        setState({
          session: null,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    }

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  return state;
}

/**
 * One active tenant membership for the signed-in user. `slug` drives the URL
 * (`/dws`, `/bestdisposal`); `name` is the human label the workspace picker
 * shows. Ordered oldest-first by the caller (`created_at`).
 */
export interface Membership {
  tenantId: string;
  slug: string;
  name: string;
  createdAt: string;
}

/**
 * MER-532: the multi-membership resolution foundation. Returns ALL active
 * memberships for the signed-in user (not `.limit(1)`), oldest-first, so the
 * app is multi-tenant-aware instead of single-tenant-assuming. RootResolver +
 * the "Choose your workspace" interstitial both read from this.
 *
 * MER-522 guards carried forward (this is the only query of `tenant_members`
 * for routing now):
 *   - `.is("revoked_at", null)`: ACTIVE memberships only, matching the RLS data
 *     policy (migration 0044). A revoked member must never be routed to a tenant
 *     they can no longer read.
 *   - `.order("created_at", { ascending: true })`: DETERMINISTIC ordering. The
 *     oldest active membership is the stable single-home / fallback choice
 *     (created_at is non-null since migration 0001).
 *
 * Rows whose joined `tenants.slug` is missing are dropped (a slug is required
 * to route). Returns `[]` when signed out, on error, or with no membership.
 */
export async function getUserMemberships(): Promise<Membership[]> {
  const supabase = getSupabase();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from("tenant_members")
    .select("tenant_id, created_at, tenants(slug, name)")
    .eq("user_id", user.id)
    .is("revoked_at", null)
    .order("created_at", { ascending: true });

  if (error || !data) return [];

  const rows = data as Array<{
    tenant_id?: string;
    created_at?: string;
    tenants?: { slug?: string; name?: string } | { slug?: string; name?: string }[] | null;
  }>;

  const memberships: Membership[] = [];
  for (const row of rows) {
    const nested = Array.isArray(row.tenants) ? row.tenants[0] : row.tenants;
    const slug = nested?.slug;
    if (!slug || !row.tenant_id) continue;
    memberships.push({
      tenantId: row.tenant_id,
      slug,
      name: nested?.name ?? slug,
      createdAt: row.created_at ?? "",
    });
  }
  return memberships;
}

/**
 * Returns the customer slug for an authenticated user's HOME, kept as a thin
 * back-compat helper over `getUserMemberships()` (oldest active membership, or
 * null). The multi-membership routing decision (single home vs. the workspace
 * picker vs. an explicit default) now lives in `resolveWorkspace`
 * (lib/workspaceRoute.mjs); this just exposes the deterministic first slug.
 */
export async function getUserCustomerSlug(): Promise<string | null> {
  const memberships = await getUserMemberships();
  return memberships[0]?.slug ?? null;
}
